// Upload model (PLAN §5 step 1, §12.4): accepted formats, pre-flight validation, detected kind and proposed
// extraction depth, content sniffing, pipeline statuses and what "cancel" means at each stage.

export type FormatFamily = 'document' | 'text' | 'dataset' | 'transcript' | 'image' | 'audio' | 'video';

export interface FormatDef {
  ext: string;
  family: FormatFamily;
  mime: string;
  label: string;
}

const MB = 1024 * 1024;

/** Largest file accepted per family (PLAN §11: oversized files rejected). */
export const MAX_BYTES: Record<FormatFamily, number> = {
  document: 200 * MB,
  text: 50 * MB,
  dataset: 100 * MB,
  transcript: 50 * MB,
  image: 50 * MB,
  audio: 1024 * MB,
  video: 2048 * MB,
};

/** Formats accepted in one batch (PLAN §12.4). Anything else is rejected in pre-flight. */
export const FORMATS: FormatDef[] = [
  { ext: 'pdf', family: 'document', mime: 'application/pdf', label: 'PDF' },
  {
    ext: 'docx',
    family: 'document',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    label: 'Word',
  },
  { ext: 'doc', family: 'document', mime: 'application/msword', label: 'Word 97–2003' },
  { ext: 'rtf', family: 'document', mime: 'application/rtf', label: 'RTF' },
  { ext: 'odt', family: 'document', mime: 'application/vnd.oasis.opendocument.text', label: 'OpenDocument' },
  { ext: 'pages', family: 'document', mime: 'application/vnd.apple.pages', label: 'Apple Pages' },
  {
    ext: 'pptx',
    family: 'document',
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    label: 'PowerPoint',
  },
  { ext: 'html', family: 'text', mime: 'text/html', label: 'HTML' },
  { ext: 'htm', family: 'text', mime: 'text/html', label: 'HTML' },
  { ext: 'md', family: 'text', mime: 'text/markdown', label: 'Markdown' },
  { ext: 'txt', family: 'text', mime: 'text/plain', label: 'Text' },
  { ext: 'xlsx', family: 'dataset', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel' },
  { ext: 'csv', family: 'dataset', mime: 'text/csv', label: 'CSV' },
  { ext: 'json', family: 'dataset', mime: 'application/json', label: 'JSON' },
  { ext: 'srt', family: 'transcript', mime: 'application/x-subrip', label: 'Subtitles (SRT)' },
  { ext: 'vtt', family: 'transcript', mime: 'text/vtt', label: 'Subtitles (VTT)' },
  { ext: 'jpg', family: 'image', mime: 'image/jpeg', label: 'JPEG' },
  { ext: 'jpeg', family: 'image', mime: 'image/jpeg', label: 'JPEG' },
  { ext: 'png', family: 'image', mime: 'image/png', label: 'PNG' },
  { ext: 'heic', family: 'image', mime: 'image/heic', label: 'HEIC' },
  { ext: 'tif', family: 'image', mime: 'image/tiff', label: 'TIFF' },
  { ext: 'tiff', family: 'image', mime: 'image/tiff', label: 'TIFF' },
  { ext: 'webp', family: 'image', mime: 'image/webp', label: 'WebP' },
  { ext: 'mp3', family: 'audio', mime: 'audio/mpeg', label: 'MP3' },
  { ext: 'wav', family: 'audio', mime: 'audio/wav', label: 'WAV' },
  { ext: 'm4a', family: 'audio', mime: 'audio/mp4', label: 'M4A' },
  { ext: 'ogg', family: 'audio', mime: 'audio/ogg', label: 'Ogg' },
  { ext: 'mp4', family: 'video', mime: 'video/mp4', label: 'MP4' },
  { ext: 'mov', family: 'video', mime: 'video/quicktime', label: 'QuickTime' },
];

const BY_EXT = new Map(FORMATS.map(f => [f.ext, f]));
export const ACCEPT_ATTRIBUTE = FORMATS.map(f => `.${f.ext}`).join(',');

export const extensionOf = (name: string): string => {
  const base = name.split(/[\\/]/).pop() ?? name;
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
};
export const formatOf = (name: string): FormatDef | undefined => BY_EXT.get(extensionOf(name));

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < MB) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * MB) return `${(n / MB).toFixed(n < 10 * MB ? 1 : 0)} MB`;
  return `${(n / (1024 * MB)).toFixed(1)} GB`;
}

/**
 * A hint shown next to a file whose format is read with less fidelity than DOCX/PDF (PLAN §5: "the upload screen
 * suggests exporting to DOCX/PDF for best fidelity"). Null for everything else.
 */
export function formatNote(name: string): string | null {
  switch (extensionOf(name)) {
    case 'pages':
      return 'Apple Pages is read by a built-in reader (text and tables). For the best result, export it from Pages to DOCX or PDF.';
    case 'doc':
      return 'Word 97–2003: only the text is read for now. Save it as DOCX to keep tables and headings.';
    default:
      return null;
  }
}

/** Plain-language pre-flight errors; `null` when the file can be sent. */
export function validateFile(name: string, size: number): string | null {
  const format = formatOf(name);
  if (!format) {
    const ext = extensionOf(name);
    return ext
      ? `.${ext} files aren't supported. Export it to PDF or DOCX and try again.`
      : "This file has no extension, so its format can't be checked.";
  }
  if (size === 0) return 'This file is empty.';
  const max = MAX_BYTES[format.family];
  if (size > max) return `This file is ${formatBytes(size)}; the limit for ${format.label} files is ${formatBytes(max)}.`;
  return null;
}

// ── Detected kind and extraction depth (PLAN §4 documents.detected_kind, §5.0) ─────────────────────────────

export const DETECTED_KINDS = [
  'profile_sheet',
  'actor_list',
  'transcript',
  'interview_synthesis',
  'narrative_report',
  'guide_note',
  'dataset',
  'media',
  'image',
  'other',
] as const;
export type DetectedKind = (typeof DETECTED_KINDS)[number];

export const EXTRACTION_DEPTHS = ['full', 'claims', 'mentions', 'library'] as const;
export type ExtractionDepth = (typeof EXTRACTION_DEPTHS)[number];
export const EXTRACTION_DEPTH_LABELS: Record<ExtractionDepth, string> = {
  full: 'Full',
  claims: 'Claims & mentions',
  mentions: 'Mentions only',
  library: 'Library only',
};
export const DETECTED_KIND_LABELS: Record<DetectedKind, string> = {
  profile_sheet: 'Profile sheet',
  actor_list: 'Actor list',
  transcript: 'Transcript',
  interview_synthesis: 'Interview synthesis',
  narrative_report: 'Report / note',
  guide_note: 'Guide note',
  dataset: 'Dataset',
  media: 'Audio / video',
  image: 'Image / scan',
  other: 'Document',
};

/** ID-code prefixes of profile sheets (`MED_`, `ORG_`, `POL_`…): admin-configurable with task 7. */
const PROFILE_CODE = /^[A-Z]{2,5}_[A-Z0-9][A-Z0-9_-]+(?:[ _-]v?\d+(?:\.\d+)*)?$/;

/**
 * A first guess from the name and format, shown before upload and editable. The pipeline refines it after
 * reading the file (template detection, transcript markers) — this only has to be a sensible default.
 */
export function detectKind(name: string, size: number): DetectedKind {
  const format = formatOf(name);
  const base = (name.split(/[\\/]/).pop() ?? name).replace(/\.[^.]+$/, '');
  const lower = base.toLowerCase();
  if (!format) return 'other';
  if (format.family === 'audio' || format.family === 'video') return 'media';
  if (format.family === 'image') return 'image';
  if (format.family === 'transcript') return 'transcript';
  if (format.family === 'dataset') return /list|liste|pilot|actors?\b/.test(lower) ? 'actor_list' : 'dataset';
  if (PROFILE_CODE.test(base)) return 'profile_sheet';
  if (/transcri|verbatim|hinoter|otter|\binterview\b.*\b(raw|audio)\b/.test(lower)) return 'transcript';
  if (/synth[eè]s|summary|extracted/.test(lower)) return 'interview_synthesis';
  if (/guide|how[ _-]to|read[ _-]?me|method/.test(lower)) return 'guide_note';
  if (/interview|entretien/.test(lower)) return 'transcript';
  if (format.family === 'text' && size < 4 * 1024) return 'guide_note';
  if (/report|note|analys|memo|rapport/.test(lower)) return 'narrative_report';
  return 'other';
}

/**
 * PLAN §5.0: template sheet → Full; transcript or interview → Claims & mentions (what interviewees say is the evidence
 * the team works from, D372); long narrative note → Mentions only; short meta note → Library only.
 */
export function proposeDepth(kind: DetectedKind): ExtractionDepth {
  switch (kind) {
    case 'profile_sheet':
    case 'actor_list':
    case 'dataset':
      return 'full';
    case 'narrative_report':
    case 'media':
      return 'mentions';
    case 'guide_note':
      return 'library';
    default:
      return 'claims';
  }
}

// ── Content sniffing (PLAN §11: file type verified by content, not extension) ───────────────────────────────

const startsWith = (b: Uint8Array, sig: number[], at = 0) => sig.every((v, i) => b[at + i] === v);
const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, to));

/** What the first bytes say the file is (`zip`, `pdf`, `ole`, `jpeg`…, or `text`); `unknown` otherwise. */
export function sniff(head: Uint8Array): string {
  if (startsWith(head, [0x25, 0x50, 0x44, 0x46])) return 'pdf';
  if (startsWith(head, [0x50, 0x4b, 0x03, 0x04])) return 'zip';
  if (startsWith(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) return 'ole';
  if (ascii(head, 0, 5) === '{\\rtf') return 'rtf';
  if (startsWith(head, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(head, [0x89, 0x50, 0x4e, 0x47])) return 'png';
  if (startsWith(head, [0x49, 0x49, 0x2a, 0x00]) || startsWith(head, [0x4d, 0x4d, 0x00, 0x2a])) return 'tiff';
  if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP') return 'webp';
  if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WAVE') return 'wav';
  if (ascii(head, 0, 4) === 'OggS') return 'ogg';
  if (ascii(head, 0, 3) === 'ID3' || (head[0] === 0xff && ((head[1] ?? 0) & 0xe0) === 0xe0)) return 'mp3';
  if (ascii(head, 4, 8) === 'ftyp') {
    const brand = ascii(head, 8, 12);
    if (/^(heic|heix|hevc|heim|heis|mif1|msf1)$/.test(brand)) return 'heic';
    if (brand === 'qt  ') return 'mov';
    if (/^M4A/.test(brand)) return 'm4a';
    return 'mp4';
  }
  if (looksLikeText(head)) return 'text';
  return 'unknown';
}

function looksLikeText(b: Uint8Array): boolean {
  if (b.length === 0) return false;
  if (startsWith(b, [0xff, 0xfe]) || startsWith(b, [0xfe, 0xff])) return true; // UTF-16 BOM
  let control = 0;
  for (const v of b) {
    if (v === 0) return false;
    if (v < 0x09 || (v > 0x0d && v < 0x20)) control++;
  }
  return control / b.length < 0.02;
}

/** Signatures each extension may carry. ISO-BMFF brands vary between tools, so audio/video accept any `ftyp`. */
const EXPECTED: Record<string, string[]> = {
  pdf: ['pdf'],
  docx: ['zip'],
  pptx: ['zip'],
  xlsx: ['zip'],
  odt: ['zip'],
  pages: ['zip'],
  doc: ['ole', 'rtf'],
  rtf: ['rtf'],
  html: ['text'],
  htm: ['text'],
  md: ['text'],
  txt: ['text'],
  csv: ['text'],
  json: ['text'],
  srt: ['text'],
  vtt: ['text'],
  jpg: ['jpeg'],
  jpeg: ['jpeg'],
  png: ['png'],
  heic: ['heic'],
  tif: ['tiff'],
  tiff: ['tiff'],
  webp: ['webp'],
  mp3: ['mp3'],
  wav: ['wav'],
  m4a: ['m4a', 'mp4'],
  ogg: ['ogg'],
  mp4: ['mp4', 'mov', 'm4a'],
  mov: ['mov', 'mp4'],
};

export function contentMatchesExtension(name: string, head: Uint8Array): boolean {
  const expected = EXPECTED[extensionOf(name)];
  return !!expected?.includes(sniff(head));
}

// ── Multipart upload parameters ──────────────────────────────────────────────────────────────────────────

/** R2 multipart parts must be ≥ 5 MiB except the last; every part but the last has this size. */
export const PART_SIZE = 8 * MB;
/** Files uploaded at the same time (PLAN §12.4). */
export const PARALLEL_UPLOADS = 3;
export const partCount = (size: number) => Math.max(1, Math.ceil(size / PART_SIZE));
export const partLength = (size: number, n: number) => (n < partCount(size) ? PART_SIZE : size - PART_SIZE * (partCount(size) - 1));

// ── Pipeline statuses and cancel stages (PLAN §2 Review statuses, §12.4 Cancel) ────────────────────────────

export const PIPELINE_STATUSES = [
  'uploading',
  'uploaded',
  'awaiting_input',
  'processing',
  'indexed',
  'extracted',
  'in_review',
  'completed',
  'skipped_review',
  'library_only',
  'failed',
  'paused_budget',
] as const;
export type PipelineStatus = (typeof PIPELINE_STATUSES)[number];

export const PIPELINE_STATUS_LABELS: Record<PipelineStatus, string> = {
  uploading: 'Uploading',
  uploaded: 'Queued for processing',
  awaiting_input: 'Waiting for your answer',
  processing: 'Processing',
  indexed: 'Indexed in Library',
  extracted: 'Extracted',
  in_review: 'Waiting for review',
  completed: 'Completed',
  skipped_review: 'Committed without review',
  library_only: 'In the Library',
  failed: 'Failed',
  paused_budget: 'Paused: AI budget reached',
};

/** The §12.4 cancel table: what cancelling does depends on how far the file got. */
export type CancelStage = 'uploading' | 'queued' | 'processing' | 'review' | 'committed';

/**
 * Cancel stage of a document. One whose extraction depth was raised later was already in the Library, so its
 * re-run can never delete it (task 9b): it counts as committed.
 */
export const cancelStageOf = (doc: { pipelineStatus: PipelineStatus; depthRaisedAt?: Date | number | null }): CancelStage =>
  doc.depthRaisedAt ? 'committed' : cancelStage(doc.pipelineStatus);

export function cancelStage(status: PipelineStatus): CancelStage {
  switch (status) {
    case 'uploading':
      return 'uploading';
    case 'uploaded':
    case 'awaiting_input':
    case 'failed':
    case 'paused_budget':
      return 'queued';
    case 'processing':
    case 'indexed':
    case 'extracted':
      return 'processing';
    case 'in_review':
      return 'review';
    default:
      return 'committed';
  }
}

/** Cancelling asks for confirmation once AI work may have been spent on the file (PLAN §12.4). */
export const cancelNeedsConfirmation = (stage: CancelStage) => stage === 'processing' || stage === 'review';

export const CANCEL_ACTION_LABELS: Record<CancelStage, string> = {
  uploading: 'Cancel',
  queued: 'Cancel',
  processing: 'Cancel',
  review: 'Discard upload',
  committed: 'Retract source',
};

export const SENSITIVITIES = ['standard', 'confidential'] as const;
export type Sensitivity = (typeof SENSITIVITIES)[number];
export const REVIEW_MODES = ['now', 'later'] as const;
export type ReviewMode = (typeof REVIEW_MODES)[number];

/** Per-batch settings with per-file overrides (PLAN §12.4). */
export interface UploadSettings {
  sensitivity: Sensitivity;
  reviewMode: ReviewMode;
  /** Omitted = use each file's proposed depth. */
  extractionDepth?: ExtractionDepth;
}
export const DEFAULT_UPLOAD_SETTINGS: UploadSettings = { sensitivity: 'standard', reviewMode: 'now' };

/** Unanswered marking questions are processed natively, as confidential, after this long (PLAN §5 step 1b). */
export const MARKING_ANSWER_TIMEOUT_MS = 24 * 60 * 60 * 1000;
/** An upload with no new part for this long is abandoned: its parts are discarded (R2 also drops them after 7 days). */
export const STALE_UPLOAD_MS = 3 * 24 * 60 * 60 * 1000;
